import * as React from 'react'
import { TimePicker as TimePickerAntd, ConfigProvider } from 'antd'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { FormControl, InputLabel, makeStyles } from '@material-ui/core'
import { styled } from '@material-ui/core/styles'
import { useState } from 'react'
import { convertDateFnsToDayjsFormat } from '../../utils/dates'
import '../../antd-custom.css'

dayjs.extend(customParseFormat)

export interface ITimePickerProps {
  id: string
  name?: string
  value: string | Date | null | undefined
  setValue?: (value: string) => void
  style?: React.CSSProperties
  className?: string
  use12Hours?: boolean
  displayFormat: string // date-fns format
  valueFormat: string // date-fns format
  size?: 'small' | 'middle' | 'large'
  label?: string
  onChange?: (time: Date | null) => void
  required?: boolean
  disabled?: boolean
  width?: string | number
  shrinkLabel?: boolean
  inputReadOnly?: boolean
}

const useStyles = makeStyles({
  customPickerLabel: {
    position: 'absolute',
    left: 12,
    transformOrigin: 'top left',
    transition: 'all 0.2s ease-out',
    padding: '0 4px',
    zIndex: 1,
    pointerEvents: 'none'
  }
})

const StyledTimePicker = styled(TimePickerAntd)({
  borderRadius: '4px',
  boxSizing: 'border-box',
  width: '100%',
  '&:hover': {
    borderColor: '#223d79'
  },
  '&.ant-picker-focused': {
    borderColor: '#223d79',
    boxShadow: 'unset',
    borderWidth: '2px'
  }
})

function TimePicker(props: ITimePickerProps) {
  const classes = useStyles()
  const [focused, setFocused] = useState(props.shrinkLabel)

  const handleFocus = () => setFocused(true)
  const handleBlur = () => setFocused(false)

  const [popupOpen, setPopupOpen] = useState(false)

  const {
    id,
    name,
    value,
    valueFormat,
    displayFormat,
    use12Hours,
    disabled,
    size,
    width,
    style,
    className,
    required,
    label,
    onChange,
    shrinkLabel
  } = props

  const dayjsDisplayFormat = convertDateFnsToDayjsFormat(displayFormat)
  const dayjsValueFormat = convertDateFnsToDayjsFormat(valueFormat)

  const parseToDayjs = (
    input: string | Date | null | undefined
  ): Dayjs | undefined => {
    if (!input) return undefined
    if (typeof input === 'string') {
      const parsed = dayjs(input, dayjsValueFormat, true)
      return parsed.isValid() ? parsed : undefined
    } else if (input instanceof Date) {
      return dayjs(input)
    }
    return undefined
  }

  const parsedValue = React.useMemo(
    () => parseToDayjs(value),
    [valueFormat, value]
  )

  const validSize =
    size === 'small' || size === 'middle' || size === 'large' ? size : 'small'

  return (
    <div>
      {popupOpen && (
        <div
          className='modal-picker-backdrop'
          onClick={() => setPopupOpen(false)}
        />
      )}
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: '#223d79'
          }
        }}
      >
        <div className={className ?? ''} style={style}>
          <FormControl
            fullWidth
            variant='outlined'
            size='small'
            style={{
              marginTop: 0,
              maxWidth: '350px',
              width: width ?? style?.width ?? '100%'
            }}
          >
            <InputLabel
              required={required}
              id={`${id}-label`}
              shrink={shrinkLabel || !!value || focused}
              className={classes.customPickerLabel}
              style={{
                top: shrinkLabel || !!value || focused ? 0 : '50%',
                transform:
                  shrinkLabel || !!value || focused
                    ? 'translateY(-50%) scale(0.75)'
                    : 'translateY(-50%)',
                background:
                  shrinkLabel || !!value || focused
                    ? 'linear-gradient(to bottom, transparent 50%, #fff 50%)'
                    : 'transparent'
              }}
            >
              {label}
            </InputLabel>

            <StyledTimePicker
              id={id}
              name={name}
              use12Hours={use12Hours}
              format={dayjsDisplayFormat}
              value={parsedValue}
              defaultPickerValue={dayjs()}
              size={validSize}
              open={popupOpen}
              onOpenChange={setPopupOpen}
              placeholder=''
              popupClassName='modal-centered-popup time-picker-popup'
              getPopupContainer={() => document.body}
              onChange={(time: Dayjs | null) => {
                const formatted = time ? time.format(dayjsValueFormat) : ''
                console.log(formatted)
                if (onChange) {
                  onChange(time ? time.toDate() : null)
                }
                if (props.setValue) {
                  props.setValue(formatted)
                }
              }}
              onFocus={shrinkLabel ? undefined : handleFocus}
              onBlur={handleBlur}
              disabled={disabled}
              showSecond={false}
              inputReadOnly={props.inputReadOnly}
            />
          </FormControl>
        </div>
      </ConfigProvider>
    </div>
  )
}

export default TimePicker
